import React, { useEffect, useRef } from 'react';
import { Box, styled } from '@mui/material';
import useSessionProgress from './useSessionProgress';

const STATUS_DISPLAY = {
  connected: { color: '#31a633', label: 'Live' },
  polling: { color: '#ff9d0b', label: 'Updating...' },
  connecting: { color: '#ff9d0b', label: 'Connecting...' },
  reconnecting: { color: '#b00020', label: 'Reconnecting...' },
  disconnected: { color: '#b00020', label: 'Disconnected' },
  idle: { color: '#9e9e9e', label: '' },
};

const TERMINAL_LABELS = {
  SUCCESS: 'Complete',
  SUCCESS_END: 'Complete',
  FAIL: 'Failed',
  TERMINATED: 'Terminated',
};

const IndicatorContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSplitView',
})(({ isSplitView }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '4px 12px',
  height: '5px',
  boxSizing: 'content-box',
  borderTopLeftRadius: isSplitView ? '8px' : '0px',
  borderTopRightRadius: isSplitView ? '8px' : '0px',
}));

const StatusDot = styled('span', {
  shouldForwardProp: (prop) => prop !== 'dotColor',
})(({ dotColor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotColor,
  display: 'inline-block',
  flexShrink: 0,
}));

const StatusLabel = styled('span')({
  fontSize: '11px',
  color: '#666',
  whiteSpace: 'nowrap',
});

function SessionPolling({
  sessionId,
  isPolling,
  setIsPolling,
  onStatusChanged,
  onSessionComplete,
  isSplitView,
}) {
  const { connectionState, componentStatuses, sessionStatus, isTerminal } =
    useSessionProgress({ sessionId, enabled: isPolling });

  const prevSignatureRef = useRef(null);

  // When SSE/polling detects a status change, tell parent to fetch full data
  useEffect(() => {
    if (!componentStatuses) return;
    const sig = JSON.stringify(componentStatuses);
    if (sig === prevSignatureRef.current) return;
    prevSignatureRef.current = sig;

    onStatusChanged();
  }, [componentStatuses, onStatusChanged]);

  // Handle terminal state
  useEffect(() => {
    if (isTerminal) {
      setIsPolling(false);
      if (onSessionComplete) {
        onSessionComplete();
      }
    }
  }, [isTerminal, setIsPolling, onSessionComplete]);

  // Derive display state
  let dotColor, label;
  if (isTerminal) {
    dotColor = '#9e9e9e';
    label = TERMINAL_LABELS[sessionStatus] || 'Complete';
  } else {
    const display = STATUS_DISPLAY[connectionState] || STATUS_DISPLAY.idle;
    dotColor = display.color;
    label = display.label;
  }

  if (!isPolling && !isTerminal) return null;

  return (
    <IndicatorContainer isSplitView={isSplitView}>
      <StatusDot dotColor={dotColor} />
      {label && <StatusLabel>{label}</StatusLabel>}
    </IndicatorContainer>
  );
}

export default SessionPolling;
